/**
 * Laboratoriya ishi ta'rifi — JSON sxema (zod).
 *
 * Lab ishi KOD EMAS, MA'LUMOT. Yangi ish qo'shish = yangi JSON fayl.
 * Frontend shu ta'rifdan boshqaruv paneli, datchiklar, topshiriq matnini yasaydi;
 * backend esa seed + harakatlar jurnali orqali natijani qayta hisoblaydi.
 *
 * Matnlar I18nText: { 'uz-Latn': ..., 'uz-Cyrl'?: ..., ru?: ..., en?: ... }.
 * 'uz-Latn' majburiy (standart til); yetishmagan 'uz-Cyrl' avtomatik transliteratsiya qilinadi.
 */
import { z } from 'zod';

export const LOCALES = ['uz-Latn', 'uz-Cyrl', 'ru', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

export const I18nText = z
  .object({
    'uz-Latn': z.string(),
    'uz-Cyrl': z.string().optional(),
    ru: z.string().optional(),
    en: z.string().optional(),
  })
  .strict();
export type I18nText = z.infer<typeof I18nText>;

/** Ko'rsatish uchun birlik o'zgartirish: displayed = value·scale + offset */
export const DisplayTransform = z
  .object({
    scale: z.number().default(1),
    offset: z.number().default(0),
    decimals: z.number().int().min(0).max(6).default(2),
    /** Ko'rsatiladigan birlik yorlig'i: "°C", "kg/h", "kPa" */
    unit: z.string().default(''),
  })
  .strict();
export type DisplayTransform = z.infer<typeof DisplayTransform>;

/** Seed bo'yicha variant parametri */
export const VariantParam = z.union([
  z
    .object({
      min: z.number(),
      max: z.number(),
      step: z.number().positive(),
      /** Fizik birlik (hujjat uchun) */
      unit: z.string().optional(),
    })
    .strict(),
  z.object({ choices: z.array(z.union([z.number(), z.string()])).min(1) }).strict(),
]);
export type VariantParam = z.infer<typeof VariantParam>;

export const Control = z
  .object({
    id: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]*$/),
    /** Kirishlar ichidagi yo'l (SI birlikda), masalan "hot.massFlow" */
    path: z.string(),
    label: I18nText,
    /** SI birlikda chegaralar */
    min: z.number(),
    max: z.number(),
    step: z.number().positive(),
    display: DisplayTransform.default({}),
    kind: z.enum(['slider', 'number', 'toggle', 'select']).default('slider'),
    /** select uchun */
    options: z.array(z.object({ value: z.number(), label: I18nText })).optional(),
  })
  .strict();
export type Control = z.infer<typeof Control>;

export const Sensor = z
  .object({
    id: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]*$/),
    /**
     * Manba yo'li: "outputs.<field>" (qadam chiqishlari), "state.<field>",
     * "inputs.<path>" (o'lchanadigan kirish, masalan kirish harorati), "steady.<field>"
     */
    source: z.string(),
    label: I18nText,
    display: DisplayTransform.default({}),
    /** O'lchov shovqini — standart og'ish, SI birlikda (0 = ideal datchik) */
    noiseStd: z.number().min(0).default(0),
    kind: z
      .enum(['temperature', 'flow', 'pressure', 'level', 'concentration', 'power', 'generic'])
      .default('generic'),
    /** Mnemosxemada joylashuv identifikatori (template ichidagi slot) */
    slot: z.string().optional(),
    /** Jurnalga yoziladimi (false = faqat ko'rsatiladi) */
    recorded: z.boolean().default(true),
  })
  .strict();
export type Sensor = z.infer<typeof Sensor>;

export const Simulation = z
  .object({
    mode: z.enum(['dynamic', 'steady']),
    /** Bir tikda simulyatsiya vaqti, s */
    dt: z.number().positive(),
    /** UI tik davri, ms (real vaqt tezligi = dt·1000/tickMs) */
    tickMs: z.number().positive().default(500),
    /** Maksimal simulyatsiya vaqti, s (0 = cheksiz) */
    maxTime: z.number().min(0).default(0),
    /** "Turg'un holat" mezoni: datchik o'zgarishi shu vaqt ichida shu qiymatdan kichik */
    steadyWindow: z.number().positive().default(30),
    steadyTolerance: z.number().positive().default(0.05),
  })
  .strict();

/** Hisobot maydoni uchun etalon qiymat manbai (server qayta hisoblaydi) */
export const Reference = z.discriminatedUnion('from', [
  z
    .object({
      from: z.literal('journalSteady'),
      /** Turg'un chiqishlar yo'li, masalan "U" yoki "Q" */
      path: z.string(),
      aggregate: z.enum(['perRecord', 'mean', 'last', 'min', 'max']).default('perRecord'),
    })
    .strict(),
  z.object({ from: z.literal('variant'), name: z.string() }).strict(),
  z.object({ from: z.literal('params'), path: z.string() }).strict(),
  z.object({ from: z.literal('constant'), value: z.number() }).strict(),
]);
export type Reference = z.infer<typeof Reference>;

export const ReportField = z
  .object({
    id: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]*$/),
    label: I18nText,
    hint: I18nText.optional(),
    display: DisplayTransform.default({}),
    /** 'number' — bitta son; 'table' — har jurnal yozuvi uchun son (perRecord) */
    kind: z.enum(['number', 'table']).default('number'),
    reference: Reference,
    tolerance: z
      .object({ rel: z.number().min(0).optional(), abs: z.number().min(0).optional() })
      .strict(),
    points: z.number().min(0),
  })
  .strict();
export type ReportField = z.infer<typeof ReportField>;

/** Bilim testi savoli (bench lablari; to'g'ri javob mijozga yuborilmaydi) */
export const QuizQuestion = z
  .object({
    id: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]*$/),
    text: I18nText,
    options: z.array(I18nText).min(2),
    /** To'g'ri variant indeksi (mijozga yuborilmaydi) */
    correct: z.number().int().min(0).optional(),
    points: z.number().min(0).default(1),
    explanation: I18nText.optional(),
  })
  .strict();
export type QuizQuestion = z.infer<typeof QuizQuestion>;

export const Quiz = z
  .object({
    questions: z.array(QuizQuestion).min(1),
    /** Tajribani boshlashdan oldin javob berish shart */
    beforeStart: z.boolean().default(true),
  })
  .strict();
export type Quiz = z.infer<typeof Quiz>;

export const MeasurementRules = z
  .object({
    /** Kamida shuncha jurnal yozuvi */
    minRecords: z.number().int().min(1).default(1),
    /** Shu boshqaruv elementi kamida shuncha xil qiymatda bo'lishi kerak */
    distinctControl: z
      .object({ controlId: z.string(), minDistinct: z.number().int().min(1) })
      .strict()
      .optional(),
    /** Yozuv turg'un holatda olinishi shart (replay tekshiradi) */
    requireSteady: z.boolean().default(true),
    /** Jarayon uchun ballar */
    points: z.number().min(0).default(0),
  })
  .strict();

export const LabDefinition = z
  .object({
    id: z.string().regex(/^[a-z][a-z0-9-]*$/),
    version: z.number().int().min(1),
    /** Apparat modeli identifikatori: 'heat-exchanger', 'cstr', ... */
    unit: z.string(),
    title: I18nText,
    subject: I18nText,
    objective: I18nText,
    /** Nazariy qism (markdown) */
    theory: I18nText,
    /** Bajarish tartibi — qadamlar */
    procedure: z.array(I18nText).min(1),
    /** Apparat parametrlari (SI). "$name" qiymatlari variantdan olinadi */
    params: z.record(z.unknown()),
    /** Boshlang'ich kirishlar (SI). "$name" qiymatlari variantdan olinadi */
    inputs: z.record(z.unknown()),
    variant: z.record(VariantParam).default({}),
    controls: z.array(Control).min(1),
    sensors: z.array(Sensor).min(1),
    simulation: Simulation,
    schematic: z
      .object({
        /** Frontend'dagi mnemosxema shabloni (odatda unit id bilan bir xil) */
        template: z.string(),
        /** Sxemadagi matnlar */
        labels: z.record(I18nText).default({}),
      })
      .strict(),
    measurements: MeasurementRules.default({}),
    report: z
      .object({
        fields: z.array(ReportField).min(1),
        conclusion: z
          .object({ required: z.boolean().default(true), points: z.number().min(0).default(0) })
          .strict()
          .default({}),
      })
      .strict(),
    grading: z
      .object({
        /** O'tish uchun minimal ball (umumiy ballning ulushi 0..1) */
        passFraction: z.number().min(0).max(1).default(0.6),
      })
      .strict()
      .default({}),
  })
  .strict();
export type LabDefinition = z.infer<typeof LabDefinition>;
export type LabDefinitionInput = z.input<typeof LabDefinition>;

export function parseLab(json: unknown): LabDefinition {
  return LabDefinition.parse(json);
}

export function safeParseLab(json: unknown): ReturnType<typeof LabDefinition.safeParse> {
  return LabDefinition.safeParse(json);
}

/** Umumiy maksimal ball */
export function maxScore(lab: LabDefinition): number {
  const fields = lab.report.fields.reduce((s, f) => s + f.points, 0);
  return fields + lab.measurements.points + lab.report.conclusion.points;
}
